/**
 * Dial lifestyle-connectors barrel. Subset relevant to relationship graph
 * (contacts + calendar + interaction history + co-travellers + work-circle).
 */
export type {
  ConnectorId,
  ConnectorPurpose,
  ConnectorState,
  ConnectMeta,
  Connector,
  ConsentReceipt,
  Mode,
  Status,
  Paise,
  Provenance,
  ContactSeed,
  CalendarEvent,
  InteractionStat,
  WorkProfile,
  TripRecord,
  TravelBooking,
  Subscription,
} from './types.ts';
export { paise } from './types.ts';

export { GmailOAuth, GOOGLE_SCOPES, ensureFreshToken, gmailFetch } from './gmail-oauth.ts';
export type { OAuthToken, OAuthMode, GmailOAuthOpts } from './gmail-oauth.ts';

export { fetchEvents as fetchCalendarEvents } from './gcalendar.ts';
export { fetchContacts as fetchGContacts } from './gcontacts.ts';
export { fetchAppleContactsNative, parseVcard } from './apple-contacts-capacitor.ts';
export { parseWaChat } from './wa-chat-export.ts';
export { parseLinkedInZip } from './linkedin-takeout-parser.ts';
export type { LinkedInImport } from './linkedin-takeout-parser.ts';
export { fetchTrips } from './uber-ola-parser.ts';
export { fetchTravel } from './mmt-travel-parser.ts';
export { detectSubscriptions } from './subscription-detector.ts';

import type { ConnectMeta } from './types.ts';

export const DIAL_CONNECTORS: readonly ConnectMeta[] = [
  {
    id: 'gcalendar',
    title_en: 'Google Calendar',
    title_hi: 'गूगल कैलेंडर',
    description: 'Birthdays, anniversaries, and event invitees become calm-tile nudges.',
    purposes: ['relationships.calendar_birthdays'],
    scopes: ['https://www.googleapis.com/auth/calendar.readonly'],
    retention_days: 365,
    mode_default: 'mock',
    v0_status: 'mock_only',
  },
  {
    id: 'gcontacts',
    title_en: 'Google Contacts',
    title_hi: 'गूगल कॉन्टैक्ट्स',
    description: 'Seeds the inner-circle graph with phones, emails, photos, and birthdays.',
    purposes: ['relationships.contacts_seed'],
    scopes: ['https://www.googleapis.com/auth/contacts.readonly'],
    retention_days: 365,
    mode_default: 'mock',
    v0_status: 'mock_only',
  },
  {
    id: 'apple-contacts',
    title_en: 'Apple Contacts',
    title_hi: 'ऐप्पल कॉन्टैक्ट्स',
    description: 'Native iOS import via Capacitor. Web fallback: drop a .vcf file.',
    purposes: ['relationships.contacts_seed'],
    scopes: ['contacts'],
    retention_days: 365,
    mode_default: 'mock',
    v0_status: 'file_drop',
  },
  {
    id: 'wa-chat-export',
    title_en: 'WhatsApp chat export',
    title_hi: 'वॉट्सऐप एक्सपोर्ट',
    description: 'Drop a chat-export ZIP. Per-month in/out counts and longest-gap derived locally. No raw messages stored.',
    purposes: ['relationships.interaction_balance'],
    scopes: [],
    retention_days: 365,
    mode_default: 'mock',
    v0_status: 'file_drop',
  },
  {
    id: 'linkedin',
    title_en: 'LinkedIn data takeout',
    title_hi: 'लिंक्डइन डेटा',
    description: 'Drop the LinkedIn export ZIP. Connections + positions seed the work-circle tag.',
    purposes: ['relationships.work_circle_seed'],
    scopes: [],
    retention_days: 730,
    mode_default: 'mock',
    v0_status: 'file_drop',
  },
  {
    id: 'uber-ola',
    title_en: 'Uber + Ola + BluSmart',
    title_hi: 'राइड्स',
    description: 'Co-travellers in airport rides land in your warm-tier graph.',
    purposes: ['relationships.travel_partners'],
    scopes: ['https://www.googleapis.com/auth/gmail.readonly'],
    retention_days: 365,
    mode_default: 'mock',
    v0_status: 'mock_only',
  },
  {
    id: 'mmt-travel',
    title_en: 'MakeMyTrip + IRCTC',
    title_hi: 'यात्रा',
    description: 'Travel partners and shared trips enrich relationship intensity.',
    purposes: ['relationships.travel_partners'],
    scopes: ['https://www.googleapis.com/auth/gmail.readonly'],
    retention_days: 730,
    mode_default: 'mock',
    v0_status: 'mock_only',
  },
];
